export interface ProfileInfo {
  PanNo: string;
  PanCardName: string;
  ProfileId: number;
  Title: string;
  FirstName: string;
  MiddleName: string;
  LastName: string;
  FamilyName: string;
  EmailId: string;
  PhoneNo: string;
  DateOfBirth: string;
  RMName: string;
  RMEmail: string;
  RMPhone: string;
  RMAvatar: string;
  SMName: string;
  SMEmail: string;
  SMPhone: string;
  SMAvatar: string;
}

export interface AMCData {
  AMCName: string;
  AMCCode: string;
  Networth: NetworthData;
  Scheme: SchemeData[];
  Transactions: TransactionData[];
  Holdings: HoldingData[];
  HoldingsReport: HoldingsReportData;
}
export interface ReportItem {
  color: string;
  name: string;
  percentage: number;
  value: number;
}
export interface HoldingsReportData {
  Sector: SectorItem[];
  Category: CategoryItem[];
  Company: ReportItem[];
  SecurityType: ReportItem[];
}

export interface CategoryItem {
  color: string;
  name: string;
  percentage: number;
  value: number;
}

export interface SectorItem {
  color: string;
  name: string;
  percentage: number;
  value: number;
}

export interface HoldingData {
  ISIN: string;
  SecurityType: string;
  CompanyName: string;
  Category: string;
  Sector: string;
  ValuationDate: string;
  HoldingDate: string;
  PortfolioWeightage: number;
  PortfolioValue: number;
}

export interface TransactionData {
  Scheme: string;
  AMC: string;
  OrderDate: string;
  ValuationDate: string;
  OrderType: string;
  TransactionType: string;
  Type: number;
  Amount: number;
  AmountToCalculate: number;
}

export interface NetworthData {
  AsOnDate: string;
  Initial: number;
  Addition: number;
  Withdrawn: number;
  Investment: number;
  CurrentNetworth: number;
  GainLoss: number;
  Return: number;
  Xirr: number;
  Irr: number;
  Realized: number;
}

export interface SchemeData {
  SchemeId: number;
  Scheme: string;
  SchemeCode: string;
  AssetClass: string;
  Category: string;
  AMC: string;
  AMCCode: string;
  InceptionDate: string;
  ClientCode: string;
  AccountCode: string;
  DPNo: string;
  TotalClients: number;
  Networth: NetworthData;
  SchemeTransactions: TransactionData[];
  SchemeHoldings: HoldingData[];
  SchemeHoldingsReport: HoldingsReportData;
}

export interface ProfileData {
  Info: ProfileInfo;
  AMC: AMCData[];
  Schemes: SchemeData[];
  Transactions: TransactionData[];
  Holdings: HoldingData[];
  HoldingsReport: HoldingsReportData;
  AssetClassReport: AssetClassReportData;
  Networth: NetworthData;
}

export interface AssetClassReportData {
  Asset: AssetReportItem[];
  Category: AssetReportItem[];
}

export interface AssetReportItem {
  Key: string;
  Sum: number;
}

export interface ChartData {
  name: string;
  value: number;
  percentage: string | number;
  color?: string;
}

export interface DashboardData {
  DisplayName: string;
  Title: string;
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNo: string;
  Role: string;
  Avatar: string;
  Country: string;
  State: string;
  City: string;
  Pincode: string;
  CurrentProfile: string;
  Profile: ProfileData;
  AllProfiles: any[];
}

export const SECTOR_COLORS = [
  '#118AB2',
  '#06D6A0',
  '#FFD166',
  '#EF476F',
  '#8E44AD',
  '#F39C12',
  '#27AE60',
  '#E74C3C',
  '#3498DB',
  '#9B59B6',
];

export const CATEGORY_COLORS = ['#118AB2', '#06D6A0', '#FFD166', '#EF476F'];

export const SECURITY_TYPE_COLORS = ['#118AB2', '#06D6A0'];

export interface PMSPortfolioSummary {
  // pan_no: string;
  // investment: number;
  // currentNetworth: number;
  // gainLoss: number;
  // xirr: number; // decimal fraction (e.g., -0.016 -> -1.6%)
  inflow: number;
  outflow: number;
  amcCount: number;
  strategies: number;
}

export interface PMSPortfolioSummaryAP {
  investment: number;
  currentValue: number;
  totalReturn: number;
  xirr: number;
}

export interface PMSPortfolioSummaryResponse {
  success: boolean;
  data: PMSPortfolioSummary;
  message?: string;
}

export interface PMSPortfolioSummaryAPResponse {
  statusCode: number;
  data: PMSPortfolioSummaryAP;
  message: string;
  success: boolean;
}

// Holdings types
export interface PMSHoldingRow {
  pan_no: string;
  isin: string;
  holding_date: string; // ISO string
  security_type: string;
  company_name: string | null;
  category: string | null;
  sector: string | null;
  valuation_date: string; // ISO string
  portfolio_weightage: number; // percent value
  portfolio_value: number; // currency value
}

export interface PMSHoldingsPagedResponse {
  success: boolean;
  data: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    data: PMSHoldingRow[];
  };
  message?: string;
}

// Sector Holdings types
export interface PMSSectorHolding {
  pan_no: string;
  key: string; // sector name
  average: number; // fraction (e.g., 0.122 => 12.2%)
}

export interface PMSSectorHoldingsResponse {
  success: boolean;
  data: PMSSectorHolding[];
  message?: string;
}

// Category Summary types
export interface PMSCategorySummary {
  key: string; // category name (e.g., "Cash", "Large Cap", "Mid Cap", "Small Cap")
  average: number; // fraction (e.g., 0.4359 => 43.59%)
}

export interface PMSCategorySummaryResponse {
  statusCode: number;
  data: PMSCategorySummary[];
  message: string;
  success: boolean;
}

// Multiple Benchmark types
export interface BenchmarkRequest {
  code: string;
  exchangeSegment: number;
}

export interface MultipleBenchmarkRequest {
  benchmarks: BenchmarkRequest[];
  year: number;
  source?: string;
}

export interface BenchmarkPerformance {
  xirr: number;
  totalReturn: number;
  absoluteReturn: number;
  start_price: number;
  end_price: number;
}

export interface BenchmarkReturnsData {
  period: string;
  date_range: {
    from: string;
    to: string;
  };
  total_records: number;
  performance: BenchmarkPerformance;
  data_available: boolean;
}

export interface BenchmarkData {
  benchmark_code: string;
  exchange_segment: number;
  benchmark_name: string;
  financial_year: string;
  financial_year_dates: {
    end: string;
  };
  calculation_date: string;
  total_api_records: number;
  returns: {
    [key: string]: BenchmarkReturnsData; // 1m, 2m, 3m, etc.
  };
}

export interface MultipleBenchmarkResponse {
  statusCode: number;
  data: {
    request_info: {
      total_benchmarks_requested: number;
      successful_benchmarks: number;
      failed_benchmarks: number;
      financial_year: string;
      processing_details: Array<{
        benchmark_code: string;
        exchange_segment: number;
      }>;
    };
    data: BenchmarkData[];
    failed_results: any[];
    timestamp: string;
  };
  message: string;
  success: boolean;
}

// Benchmark Return types
export interface PMSBenchmarkReturn {
  scheme_id: number;
  return_1_month: number | null;
  return_3_months: number | null;
  return_6_months: number | null;
  return_1_yr: number | null;
  return_2_yr: number | null;
  return_3_yr: number | null;
  return_5_yr: number | null;
  return_10_yr: number | null;
  return_inception: number | null;
  created_at: string;
  updated_at: string;
}

export interface PMSBenchmarkReturnResponse {
  statusCode: number;
  data: PMSBenchmarkReturn;
  message: string;
  success: boolean;
}

// Portfolio Performance types
export interface PMSPortfolioPerformance {
  January: number;
  February: number;
  March: number;
  April: number;
  May: number;
  June: number;
  July: number;
  August: number;
  September: number;
  October: number;
  November: number;
  December: number;
}

export interface PMSPortfolioPerformanceResponse {
  statusCode: number;
  data: PMSPortfolioPerformance;
  message: string;
  success: boolean;
}

// Transaction types
export interface PMSTransaction {
  id: number;
  pan_no: string;
  scheme: string;
  amc: string;
  order_date: string; // ISO string
  valuation_date: string; // ISO string
  order_type: string;
  transaction_type: string;
  type: number;
  amount: number;
  amount_to_calculate: number;
  created_at: string; // ISO string
  updated_at: string; // ISO string
}

export interface PMSTransactionSummary {
  total_amount: number;
  total_purchase: number;
  total_redemption: number;
  total_transactions: number;
}

//Transaction types PMS Dashboard
export interface PMSDashboardTransaction {
  Scheme: string;
  AMC: string;
  OrderDate: string; // ISO Date string
  ValuationDate: string; // ISO Date string
  OrderType: string;
  TransactionType: string;
  Type: number;
  Amount: number;
  AmountToCalculate: number;
}

// Top Sector Holdings types
export interface TopSectorHolding {
  scheme_id: number;
  name: string;
  value: number;
  created_at: string;
  updated_at: string;
}

export interface TopSectorHoldingsResponse {
  statusCode: number;
  data: {
    totalCount: number;
    data: TopSectorHolding[];
  };
  message: string;
  success: boolean;
}

// Top Company Holdings types
export interface TopCompanyHolding {
  scheme_id: number;
  name: string;
  value: number;
  created_at: string;
  updated_at: string;
}

export interface TopCompanyHoldingsResponse {
  statusCode: number;
  data: {
    totalCount: number;
    data: TopCompanyHolding[];
  };
  message: string;
  success: boolean;
}

export interface PMSTransactionsPagedResponse {
  statusCode: number;
  data: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    data: PMSTransaction[];
    summary: PMSTransactionSummary;
  };
}

// Scheme types
export interface PMSScheme {
  schemeId: number;
  scheme: string;
  schemeCode: string;
  assetClass: string;
  category: string;
  amc: string;
  amcCode: string;
  inceptionDate: string; // ISO string
  totalClients: number;
  minInvestment?: number;
  status?: string;
  description?: string;
  [key: string]: any; // For any additional fields
}

export interface PMSSchemesPagedResponse {
  statusCode: number;
  data: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    data: PMSScheme[];
    summary: {
      bestPerformer: string;
      minInvestment: number;
      totalSchemes: string;
    };
  };
  message: string;
  success: boolean;
}

export interface PMSSchemeDetailMaster {
  schemeId: number;
  schemeCode: string;
  schemeName: string;
  assetClass: string;
  categoryId: number;
  category: string;
  reportingStructure: string;
}

export interface PMSSchemeDetailInformation {
  schemeId: number;
  minimumInvestment: number;
  aumInCrs: number;
  avgMarketCap: number;
  benchmark: string;
  inceptionDate: string;
  feeProfitShare: string;
  feeFixedAmc: string;
  feeVariableAmc: string;
  feeHurdle: string;
  exitLoad_1Yr: string;
  exitLoad_2Yr: string;
  exitLoad_3Yr: string;
  sipOption: string;
  stpOption: string;
  investmentObjective: string;
  investmentApproach: string;
  asOnDate: Record<string, unknown>;
}

export interface PMSSchemeDetailAmc {
  schemeId: number;
  name: string;
  code: string;
  logo: string;
  address: string;
  aumInCrs: number;
  asOnDate: Record<string, unknown>;
}

export interface PMSSchemeDetailReturns {
  schemeId: number;
  return_1Month: number;
  return_3Months: number;
  return_6Months: number;
  return_1Yr: number;
  return_2Yr: number;
  return_3Yr: number;
  return_5Yr: number;
  return_10Yr: number;
  returnInception: number;
}

export interface PMSSchemeDetailBenchmarkReturns {
  schemeId: number;
  return_1Month: number;
  return_3Months: number;
  return_6Months: number;
  return_1Yr: number;
  return_2Yr: number;
  return_3Yr: number;
  return_5Yr: number;
  return_10Yr: number;
  returnInception: number;
}

export interface PMSSchemePerformanceEntry {
  schemeId: number;
  name: string;
  value: number;
}

export interface PMSSchemeHoldingEntry {
  schemeId: number;
  name: string;
  value: number;
}

export interface PMSSchemeFundManager {
  schemeId: number;
  id: number;
  code: string;
  name: string;
  image: string;
  experience: string;
}

export interface PMSSchemeDetailData {
  master: PMSSchemeDetailMaster;
  information: PMSSchemeDetailInformation;
  amc: PMSSchemeDetailAmc;
  returns: PMSSchemeDetailReturns;
  benchmarkReturns: PMSSchemeDetailBenchmarkReturns;
  calendarYearPerformance: PMSSchemePerformanceEntry[];
  financialYearPerformance: PMSSchemePerformanceEntry[];
  topHoldingCompany: PMSSchemeHoldingEntry[];
  topHoldingSector: PMSSchemeHoldingEntry[];
  allocation: PMSSchemeHoldingEntry[];
  fundManagers: PMSSchemeFundManager[];
}

export interface PMSSchemeSuggestionsResponse {
  statusCode: number;
  data: PMSSchemeDetailData[];
  message: string;
  success: boolean;
}

export interface PMSSchemeDetailResponse {
  statusCode: number;
  data: PMSSchemeDetailData;
  message: string;
  success: boolean;
}

// Import dashboard types

// Dashboard types
export interface PMSDashboardData {
  currentNetworth: number;
  investment: number;
  gainLoss: number;
  xirr: number; // decimal fraction (e.g., -0.016 -> -1.6%)
  amcCount?: number;
  // Full dashboard data from API
  fullData?: DashboardData;
  // Chart-ready data
  schemeSectorChart?: ChartData[][];
  schemeCategoryChart?: ChartData[][];
  schemeSecurityChart?: ChartData[][];
  // Portfolio chart format (for pie charts)
  sectorPortfolioData?: any[];
  categoryPortfolioData?: any[];
  securityTypePortfolioData?: any[];
  schemeHoldingRaw?: any[]; // Raw scheme holdings data from API
  holdings?: HoldingData[];
  schemeIds?: (number | string)[];
  schemeName?: string[];
  transactions?: PMSDashboardTransaction[];
  schemeTransactions?: PMSDashboardTransaction[]; // Scheme-specific transactions
  amcData?: AMCData[];
}

export interface PMSDashboardResponse {
  success: boolean;
  data: PMSDashboardData;
  message?: string;
}

export interface PortfolioPerformanceSummaryProps {
  clientCode: string;
  financeYear?: number;
  className?: string;
  compact?: boolean;
}

export interface SchemeOption {
  id: number | string;
  name: string;
  amc?: string;
}

export interface BenchmarkPerformanceChartProps {
  benchmarks: BenchmarkRequest[];
  title?: string;
  className?: string;
  defaultInterval?: string;
  schemes?: SchemeOption[];
  selectedSchemeIds?: (number | string)[];
  onSchemeSelectionChange?: (schemeIds: (number | string)[]) => void;
  onRetry?: () => void;
  // Parent component states
  isError?: boolean;
  isLoading?: boolean;
  error?: any;
  // Optional client identifier to scope scheme benchmark calls
  clientCode?: string;
}

export type TimeInterval = '1M' | '3M' | '6M' | '1Y' | '3Y' | '5Y';

export interface ChartDataPoint {
  name: string;
  fullName: string;
  benchmark_code: string;
  m1: number;
  m3: number;
  m6: number;
  y1: number;
  y3: number;
  y5: number;
  alpha: number;
  highlight?: boolean;
}

export interface HoldingsTableProps {
  data?: any[];
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  error?: any;
  onRefresh?: () => void;
  className?: string; // Optional external transaction data
  schemeNames?: string[]; // Available scheme names for dropdown
  selectedScheme?: string; // Currently selected scheme
  onSchemeChange?: (scheme: string) => void; // Callback for scheme change
}

export interface CategoryData {
  key: string;
  average: number;
}

export interface MarketCapAllocationChartProps {
  data: CategoryData[];
  isLoading?: boolean;
  isError?: boolean;
  title?: string;
  chartType?: 'pie' | 'bar';
  className?: string;
  noWrapper?: boolean;
  // Enhanced error handling props
  onRetry?: () => void;
  error?: any;
}

export interface PortfolioAllocationChartProps {
  data: CategoryData[];
  isLoading?: boolean;
  isError?: boolean;
  title?: string;
  className?: string;
  chartType?: 'bar' | 'pie';
  noWrapper?: boolean;
  onRetry?: () => void;
}

export interface PortfolioPerformanceChartProps {
  clientCode: string;
  defaultFinanceYear?: number;
  chartType?: 'line' | 'area' | 'bar';
  className?: string;
  noWrapper?: boolean;
}

export interface ChartDataItem {
  month: string;
  monthIndex: number;
  fullMonth: string;
  value: number;
  displayValue: string;
  isActive: boolean;
  color: string;
}

export interface CompanyData {
  name: string;
  value: number;
  percentage: string;
  change?: string;
  sector?: string;
}

export interface SectorData {
  name: string;
  value: number;
  percentage: string;
  change?: string;
}

export interface TransactionTableProps {
  clientCode?: string;
  schemeNames?: string[];
  selectedScheme?: string;
  onSchemeChange?: (scheme: string) => void;
}
